import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ClerkProvider } from '@clerk/clerk-react';
import { frFR } from '@clerk/localizations';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Workbox } from 'workbox-window';
import './styles.css';
import { App } from './App';
import { capturePrefillFromUrl } from './features/leads/wizard/prefill';
import { ErrorBoundary } from './components/ErrorBoundary';
import { toast } from './components/Toaster';
import { initAnalytics, track } from './lib/analytics';

initAnalytics();
window.addEventListener('appinstalled', () => track('pwa_installed', {}));

// Un chunk de route qui ne se charge pas (déploiement en cours, cache
// périmé) affichait la page d'erreur anglaise du routeur. On recharge une
// fois : le nouvel `index.html` référence les bons fichiers. Une seule fois
// par minute, pour ne pas boucler si la cause est ailleurs.
window.addEventListener('vite:preloadError', (event) => {
  const key = 'doorly:preload-reload-at';
  const last = Number(sessionStorage.getItem(key) ?? 0);
  if (Date.now() - last < 60_000) return;
  event.preventDefault();
  sessionStorage.setItem(key, String(Date.now()));
  window.location.reload();
});

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!PUBLISHABLE_KEY) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY');
}

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  const wb = new Workbox('/sw.js', { type: 'module' });

  // Fenêtre "post-login" : les ~30s qui suivent un sign-in fraîchement
  // déclenché par App.tsx. Pendant cette fenêtre, on auto-reload
  // silencieusement quand une nouvelle version SW prend le contrôle —
  // l'utilisateur vient de se connecter, il est sur le splash, pas en
  // train de saisir une fiche. Hors fenêtre (= en plein wizard ou
  // LeadDetail), on bascule sur le toast manuel pour ne pas casser sa
  // saisie en cours.
  const POST_LOGIN_WINDOW_MS = 30_000;
  const isInPostLoginWindow = (): boolean => {
    const ts = (window as unknown as { __doorlyFreshLogin?: number }).__doorlyFreshLogin;
    return typeof ts === 'number' && Date.now() - ts < POST_LOGIN_WINDOW_MS;
  };

  const showUpdateToast = () => {
    if (isInPostLoginWindow()) {
      track('update_auto_reloaded_post_login', {});
      window.location.reload();
      return;
    }
    // Hors fenêtre post-login : on marque un flag global "update
    // pending". App.tsx surveille les navigations TanStack Router et
    // auto-reload à la première navigation qui sort du wizard
    // (`/leads/new`). En complément on garde le toast pour informer
    // l'utilisateur qu'une mise à jour est en route — il peut cliquer
    // pour reload immédiatement.
    (window as unknown as { __doorlySwUpdatePending?: boolean }).__doorlySwUpdatePending = true;
    toast.info('Nouvelle version disponible — la mise à jour s\'appliquera à la prochaine navigation.', {
      id: 'sw-update-available',
      duration: Number.POSITIVE_INFINITY,
      action: {
        label: 'Recharger',
        onClick: () => {
          track('update_reloaded', {});
          window.location.reload();
        },
      },
    });
  };

  if (navigator.serviceWorker.controller) {
    wb.addEventListener('waiting', showUpdateToast);
    // SW currently calls skipWaiting()+clientsClaim() so 'waiting' may not
    // fire — fallback to 'controlling', which fires once the new SW takes
    // control. Auto-reload OR toast selon la fenêtre post-login.
    wb.addEventListener('controlling', showUpdateToast);
  }

  void wb.register().then((registration) => {
    if (!registration) return;
    // Accelerate update detection: browsers only check sw.js every ~24h by
    // default. Re-check when the tab becomes visible, every 5 minutes
    // while it's open, ET dès qu'un user vient de se connecter (cf.
    // event `auth:fresh-login` émis par App.tsx).
    const check = () => void registration.update();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') check();
    });
    window.addEventListener('auth:fresh-login', check);
    window.setInterval(check, 5 * 60 * 1000);
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (failureCount, error) => {
        // Never retry auth errors — retrying 401/403 just floods the backend
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403 || status === 429) return false;
        return failureCount < 1;
      },
    },
  },
});

/**
 * Le formulaire Clerk annonce « Adresse e-mail ou nom d'utilisateur » dès que
 * `auth_username.used_for_sign_in` est actif — ce qui est le cas, et doit le
 * rester : c'est ce réglage qui rend l'identifiant matricule acceptable, y
 * compris sur `/connexion`. Le désactiver casserait la connexion vendeur.
 *
 * On corrige donc le libellé, pas la capacité. Cet écran s'adresse aux
 * managers, RP et admins, qui ont tous une adresse email ; les vendeurs ont
 * leur propre porte, signalée par un bouton juste en dessous. Mentionner le
 * nom d'utilisateur ici ne fait qu'égarer.
 *
 * Un identifiant saisi sur cet écran reste accepté par Clerk : le repli
 * fonctionne toujours pour qui se trompe de page, on cesse simplement de le
 * proposer.
 */
const localization = {
  ...frFR,
  formFieldLabel__emailAddress_username: 'Adresse e-mail',
  formFieldInputPlaceholder__emailAddress_username: 'nom@exemple.com',
};

// Lien de pré-remplissage d'une fiche (`/leads/new#nom=…`) : capté AVANT la
// connexion et retiré de la barre d'adresse (cf. features/leads/wizard/prefill.ts).
capturePrefillFromUrl();

const root = document.getElementById('root');
if (!root) throw new Error('#root element not found');

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary>
      <ClerkProvider publishableKey={PUBLISHABLE_KEY} afterSignOutUrl="/" localization={localization}>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </ClerkProvider>
    </ErrorBoundary>
  </StrictMode>,
);
